import { z } from "zod";

import {
  CustomFieldRecord,
  UpdateCustomFieldRequest,
} from "@/shared/domain/customFields";

import { descriptionSchema, displayNameSchema } from "./customFieldFormShared";

export const editCustomFieldFormSchema = z.object({
  display_name: displayNameSchema,
  description: descriptionSchema,
});

/** Inferred from the schema so the form's value type can't drift from validation. */
export type EditCustomFieldFormValues = z.infer<
  typeof editCustomFieldFormSchema
>;

/** Seed the form from the field's current values (null shows as an empty input). */
export function editFormDefaults(
  field: CustomFieldRecord,
): EditCustomFieldFormValues {
  return {
    display_name: field.display_name ?? "",
    description: field.description ?? "",
  };
}

/** Trimmed value, or `null` when empty/whitespace-only (clears the attribute). */
function normalize(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Builds the update request from the form, including only the fields that
 * actually changed (others are omitted = left unchanged). An emptied/whitespace
 * field becomes `null` (cleared). Returns `{}` when nothing changed after trim.
 */
export function buildUpdateCustomFieldRequest(
  values: EditCustomFieldFormValues,
  original: CustomFieldRecord,
): UpdateCustomFieldRequest {
  const request: UpdateCustomFieldRequest = {};

  const nextDisplayName = normalize(values.display_name);
  if (nextDisplayName !== (original.display_name ?? null)) {
    request.display_name = nextDisplayName;
  }

  const nextDescription = normalize(values.description);
  if (nextDescription !== (original.description ?? null)) {
    request.description = nextDescription;
  }

  return request;
}
